/**
 * Хуки анимаций: появление при скролле, счётчики чисел, магнитные кнопки,
 * 3D-наклон, параллакс, FLIP-перестановки и «полёт в корзину».
 * Всё уважает prefers-reduced-motion.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const coarsePointer = () =>
  typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;

/* ─────────────────  Появление при скролле  ───────────────── */

let observer = null;
const seen = new WeakSet();

function sharedObserver() {
  if (observer) return observer;
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.06 },
  );
  return observer;
}

/**
 * Включает появление для всех [data-reveal] внутри контейнера.
 * Контейнер с data-stagger="80" раздаёт детям нарастающую задержку.
 * deps — перерисовать после смены содержимого.
 */
export function useReveal(deps = []) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;

    for (const group of root.querySelectorAll('[data-stagger]')) {
      const step = Number(group.dataset.stagger) || 70;
      group.querySelectorAll('[data-reveal]').forEach((el, i) => {
        if (!el.style.getPropertyValue('--reveal-delay')) {
          el.style.setProperty('--reveal-delay', `${Math.min(i, 12) * step}ms`);
        }
      });
    }

    const io = sharedObserver();
    const targets = [...root.querySelectorAll('[data-reveal]')];
    for (const el of targets) {
      if (el.classList.contains('is-in') || seen.has(el)) continue;
      if (reducedMotion()) {
        el.classList.add('is-in');
        continue;
      }
      seen.add(el);
      io.observe(el);
    }

    // Подстраховка: если наблюдатель почему-то не сработал (фоновая вкладка,
    // отключённые анимации), через полторы секунды показываем видимое содержимое.
    const failsafe = setTimeout(() => {
      for (const el of targets) {
        if (el.classList.contains('is-in')) continue;
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) el.classList.add('is-in');
      }
    }, 1500);

    return () => clearTimeout(failsafe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return ref;
}

/** Заголовок, который выезжает построчно из-под маски. */
export function useSplitLines(active = true) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !active) return;

    const lines = [...el.querySelectorAll('.split-line')];
    if (!lines.length) return;

    if (reducedMotion()) {
      lines.forEach((l) => l.classList.add('is-in'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          lines.forEach((l) => l.classList.add('is-in'));
          io.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [active]);

  return ref;
}

/* ─────────────────────  Счётчик числа  ───────────────────── */

const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

/** Плавно докручивает число к новому значению. Возвращает ref на элемент. */
export function useCountUp(value, { duration = 620, format = (v) => String(Math.round(v)) } = {}) {
  const ref = useRef(null);
  const previous = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const from = previous.current;
    previous.current = value;

    if (from === null || reducedMotion() || from === value) {
      el.textContent = format(value);
      return;
    }

    let raf = 0;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      el.textContent = format(from + (value - from) * easeOutExpo(t));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return ref;
}

/* ─────────────────────  Магнитная кнопка  ────────────────── */

export function useMagnetic(strength = 0.3) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || reducedMotion() || coarsePointer()) return;

    let raf = 0;
    const move = (e) => {
      const r = el.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2);
      const dy = e.clientY - (r.top + r.height / 2);
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.transform = `translate(${dx * strength}px, ${dy * strength}px)`;
      });
    };
    const leave = () => {
      cancelAnimationFrame(raf);
      el.style.transform = '';
    };

    el.addEventListener('mousemove', move);
    el.addEventListener('mouseleave', leave);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('mousemove', move);
      el.removeEventListener('mouseleave', leave);
    };
  }, [strength]);

  return ref;
}

/* ────────────────────────  Наклон 3D  ────────────────────── */

export function useTilt({ max = 5 } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || reducedMotion() || coarsePointer()) return;

    let raf = 0;
    const move = (e) => {
      const r = el.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width - 0.5;
      const py = (e.clientY - r.top) / r.height - 0.5;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.transform =
          `perspective(900px) rotateX(${(-py * max).toFixed(2)}deg) rotateY(${(px * max).toFixed(2)}deg)`;
      });
    };
    const leave = () => {
      cancelAnimationFrame(raf);
      el.style.transform = '';
    };

    el.addEventListener('mousemove', move);
    el.addEventListener('mouseleave', leave);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('mousemove', move);
      el.removeEventListener('mouseleave', leave);
    };
  }, [max]);

  return ref;
}

/* ─────────────────────────  Параллакс  ───────────────────── */

export function useParallax(speed = 0.1) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || reducedMotion()) return;

    let ticking = false;
    const update = () => {
      ticking = false;
      const r = el.getBoundingClientRect();
      const offset = (r.top + r.height / 2 - window.innerHeight / 2) * -speed;
      el.style.setProperty('--parallax', `${offset.toFixed(1)}px`);
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [speed]);

  return ref;
}

/* ─────────────────  FLIP-перестановка списка  ─────────────── */

/** Запоминает позиции детей и доигрывает переезд после перерисовки. */
export function useFlip(dep) {
  const ref = useRef(null);
  const positions = useRef(new Map());

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root || reducedMotion()) return;

    for (const child of root.children) {
      const key = child.dataset.flipKey;
      const first = positions.current.get(key);
      const last = child.getBoundingClientRect();
      if (first) {
        const dx = first.left - last.left;
        const dy = first.top - last.top;
        if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
          child.animate(
            [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }],
            { duration: 460, easing: 'cubic-bezier(.16,1,.3,1)' },
          );
        }
      }
    }

    positions.current = new Map();
    for (const child of root.children) {
      positions.current.set(child.dataset.flipKey, child.getBoundingClientRect());
    }
  }, [dep]);

  return ref;
}

/* ───────────────  Полёт товара в корзину  ─────────────── */

export function flyToCart(sourceEl) {
  const target = document.getElementById('cartBtn');
  if (!sourceEl || !target || reducedMotion()) return Promise.resolve();

  const from = sourceEl.getBoundingClientRect();
  const to = target.getBoundingClientRect();
  const layer = document.getElementById('flyLayer');
  if (!layer) return Promise.resolve();

  const ghost = sourceEl.cloneNode(true);
  ghost.className = 'fly-item';
  ghost.style.cssText =
    `position:fixed;left:${from.left}px;top:${from.top}px;width:${from.width}px;height:${from.height}px`;
  layer.appendChild(ghost);

  const dx = to.left + to.width / 2 - (from.left + from.width / 2);
  const dy = to.top + to.height / 2 - (from.top + from.height / 2);

  return ghost
    .animate(
      [
        { transform: 'translate(0,0) scale(1)', opacity: 1, borderRadius: '18px' },
        { transform: `translate(${dx * 0.55}px, ${dy * 0.42 - 90}px) scale(.6)`, opacity: 0.95, offset: 0.55 },
        { transform: `translate(${dx}px, ${dy}px) scale(.06)`, opacity: 0, borderRadius: '50%' },
      ],
      { duration: 780, easing: 'cubic-bezier(.5,-0.2,.35,1)' },
    )
    .finished.then(() => ghost.remove())
    .catch(() => ghost.remove());
}

/* ────────────────────  Мелкие эффекты  ───────────────────── */

export function shake(el) {
  if (!el) return;
  el.classList.remove('is-shaking');
  void el.offsetWidth;
  el.classList.add('is-shaking');
  setTimeout(() => el.classList.remove('is-shaking'), 520);
}

export function pulse(el) {
  if (!el || reducedMotion()) return;
  el.classList.remove('is-popping');
  void el.offsetWidth;
  el.classList.add('is-popping');
  setTimeout(() => el.classList.remove('is-popping'), 480);
}

export function scrollToEl(el, offset = 90) {
  if (!el) return;
  window.scrollTo({
    top: el.getBoundingClientRect().top + window.scrollY - offset,
    behavior: reducedMotion() ? 'auto' : 'smooth',
  });
}

/* ─────────  Состояние кнопки: загрузка → успех  ────────── */

/** Возвращает [state, run] — run(asyncFn) сам переключает загрузку и галочку. */
export function useAsyncButton() {
  const [state, setState] = useState('idle'); // idle | loading | done
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const run = useCallback(async (fn, { doneMs = 1400 } = {}) => {
    setState('loading');
    try {
      const result = await fn();
      setState('done');
      timer.current = setTimeout(() => setState('idle'), doneMs);
      return result;
    } catch (err) {
      setState('idle');
      throw err;
    }
  }, []);

  return [state, run];
}
